import React from 'react';
import { motion } from 'framer-motion';
import { Users, Briefcase, Building2, GraduationCap, FileText, HeadphonesIcon, BarChart3, ArrowRight, Palette } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Nav } from '../components/Nav';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Partner Program — Earn 40% + 20% Recurring Commission
  Meta Desc: White-label MISTR for your clients. Earn 40% on first payment plus 20% recurring every month. Become the AI operations company your market needs.
  Primary KW: AI automation affiliate program
*/
export function Partners() {
  return <div className="min-h-screen bg-off-white flex flex-col" data-id="element-1859">
      <Helmet data-id="element-1860">
        <title data-id="element-1861">Partner Program | White-label MISTR | MindStream.ing</title>
        <meta name="description" content="White-label MISTR for your clients. Earn 40% on first payment plus 20% recurring every month. Become the AI operations company your market needs." data-id="element-1862" />
      </Helmet>
      <Nav data-id="element-1863" />
      <main className="pt-20" data-id="element-1864">
        {/* Hero */}
        <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-1865">
          {/* Ambient Glow Orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1866" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1867" />

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} data-id="element-1868" />

          <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1869">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-purple/20 bg-purple/5 text-sm font-poppins font-semibold tracking-wider uppercase text-purple mb-6" data-id="element-1870">
              PARTNER PROGRAM
            </span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl text-gray-900 leading-[1.1] mb-6" data-id="element-1871">
              Bring MR to Your Clients.
            </h1>
            <p className="text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-1872">
              White-label MR under your brand. 40% commission + 20% recurring.
              Become the AI operations company your market needs.
            </p>
            <button className="btn-primary py-4 px-8 text-lg shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-1873">
              Apply to Partner Program →
            </button>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1874" />

        {/* Commission Structure */}
        <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1875">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1876" />

          <div className="inner-container relative z-10" data-id="element-1877">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1878">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-gradient-to-br from-teal/10 to-transparent backdrop-blur-sm border border-teal/30 rounded-2xl p-8 text-center shadow-[0_0_30px_rgba(7,133,183,0.15)]" data-id="element-1879">
                <div className="text-6xl font-poppins font-black text-teal mb-2 drop-shadow-[0_0_15px_rgba(7,133,183,0.5)]" data-id="element-1880">
                  40%
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-1881">First Payment</h3>
                <p className="text-steel text-sm" data-id="element-1882">
                  Earn a massive 40% commission on the first month of any plan
                  your client chooses.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-gradient-to-br from-purple/10 to-transparent backdrop-blur-sm border border-purple/30 rounded-2xl p-8 text-center shadow-[0_0_30px_rgba(124,58,237,0.15)]" data-id="element-1883">
                <div className="text-6xl font-poppins font-black text-purple mb-2 drop-shadow-[0_0_15px_rgba(124,58,237,0.5)]" data-id="element-1884">
                  20%
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-1885">Recurring Monthly</h3>
                <p className="text-steel text-sm" data-id="element-1886">
                  Keep earning 20% every single month for the lifetime of the
                  client's subscription.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1887" />

        {/* Partner Models */}
        <section className="section-padding bg-dark-bg relative" data-id="element-1888">
          <div className="inner-container relative z-10" data-id="element-1889">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1890">
              <span className="eyebrow" data-id="element-1891">How We Work Together</span>
              <h2 className="h2-title" data-id="element-1892">Choose Your Partner Model</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-1893">
              {[{
              icon: <Users size={32} data-id="element-1894" />,
              title: 'Referral Partner',
              desc: 'Simply introduce your network to MindStream.ing. We handle the sales, onboarding, and support. You collect the commission.',
              color: 'text-teal',
              bg: 'bg-teal/10'
            }, {
              icon: <Briefcase size={32} data-id="element-1895" />,
              title: 'Reseller',
              desc: 'Sell MR directly to your clients as part of your service offering. We deliver the technology, you manage the relationship.',
              color: 'text-purple',
              bg: 'bg-purple/10'
            }, {
              icon: <Building2 size={32} data-id="element-1896" />,
              title: 'White-Label Agency',
              desc: 'Put your logo on MR. Your clients never see the MindStream.ing brand. You own the entire experience and pricing.',
              color: 'text-red-cta',
              bg: 'bg-red-cta/10'
            }].map((model, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all group" data-id="element-1897">
                  <div className={`${model.bg} ${model.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`} data-id="element-1898">
                    {model.icon}
                  </div>
                  <h3 className="font-poppins font-bold text-2xl mb-4" data-id="element-1899">
                    {model.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-1900">{model.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1901" />

        {/* What Partners Get */}
        <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1902">
          {/* Ambient Glow */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1903" />

          <div className="inner-container relative z-10" data-id="element-1904">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1905">
              <span className="eyebrow" data-id="element-1906">Partner Resources</span>
              <h2 className="h2-title" data-id="element-1907">Everything You Need to Win</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-1908">
              {[{
              icon: <GraduationCap size={24} data-id="element-1909" />,
              title: 'Training & Certification',
              desc: 'Master the platform with our comprehensive partner academy and get officially certified.'
            }, {
              icon: <FileText size={24} data-id="element-1910" />,
              title: 'Co-Branded Materials',
              desc: 'Access pitch decks, case studies, and marketing collateral ready for your logo.'
            }, {
              icon: <HeadphonesIcon size={24} data-id="element-1911" />,
              title: 'Dedicated Partner Support',
              desc: 'Skip the queue. Get direct access to our partner success team for technical and sales support.'
            }, {
              icon: <BarChart3 size={24} data-id="element-1912" />,
              title: 'Revenue Dashboard',
              desc: 'Track your leads, active clients, and upcoming payouts in real-time through your partner portal.'
            }].map((feature, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: i % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="flex items-start gap-4 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-teal/30 hover:shadow-[0_0_20px_rgba(7,133,183,0.1)] transition-all" data-id="element-1913">
                  <div className="text-teal shrink-0 mt-1 bg-teal/10 p-2 rounded-lg" data-id="element-1914">
                    {feature.icon}
                  </div>
                  <div data-id="element-1915">
                    <h3 className="font-poppins font-bold text-lg mb-2" data-id="element-1916">
                      {feature.title}
                    </h3>
                    <p className="text-steel text-sm" data-id="element-1917">{feature.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-16 text-center" data-id="element-1918">
              <button className="btn-primary flex items-center gap-2 mx-auto" data-id="element-1919">
                Apply to Partner Program <ArrowRight size={20} data-id="element-1920" />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>;
}